import { useCallback } from 'react'
import {
  AgxRow,
  AgxColumn,
  AgxButton,
  Icon_PlusOutline,
  Icon_PartnershipLevel,
  AgxTextInput,
  AgxTypography,
  AgxDropdown,
  Icon_CloseCircle,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import {
  PartnershipDetails,
  PartnershipDetailsPartnershipLevel,
  PartnershipTier,
  PartnershipTierLevel,
} from '@urbanx/agx.agencies.client'
import { AgreementTypeOptions } from './AgreementTypeOptions'
import { AgreementType } from './AgreementType'

import {
  PartnershipLevelOptions,
  PartnershipLevelLabels,
} from './PartnershipLevelOptions'
import { defaultPartnershipTemplates } from './defaultPartnershipTemplates'
import { tierOrder } from './tierOrder'

type Props = {
  partnershipDetails: PartnershipDetails
  onChange: (newDetails: PartnershipDetails) => void
  startingTierOption: { label: string; value: string }
  onAddTierHandler: () => void
  onDeleteItemHandler: (levelToDelete?: PartnershipTierLevel) => void
}

const PartnershipAgreement = ({
  partnershipDetails,
  onChange,
  startingTierOption,
  onAddTierHandler,
  onDeleteItemHandler,
}: Props) => {
  const handleUpdateAgreement = useCallback(
    (updatedAgreement: Partial<PartnershipDetails['partnershipAgreement']>) => {
      onChange({
        ...partnershipDetails,
        partnershipAgreement: {
          ...(partnershipDetails.partnershipAgreement || {}),
          ...updatedAgreement,
        },
      })
    },
    [partnershipDetails, onChange]
  )
  const isDefaultAgreement =
    !partnershipDetails.partnershipAgreement?.customized
  return (
    <AgxColumn gap="spacing-xl" fill>
      <AgxTypography id="agx-accordion-value" size="large" type="title">
        Partnership Agreement
      </AgxTypography>
      <Panel>
        <AgxColumn gap="spacing-4xl" fill>
          <AgxRow gap="spacing-3xl" fill>
            <AgxDropdown
              id="agreementType"
              options={AgreementTypeOptions}
              selected={
                partnershipDetails.partnershipAgreement?.customized
                  ? AgreementTypeOptions[0].value
                  : AgreementTypeOptions[1].value
              }
              label="Select agreement type"
              required
              onChange={(value) => {
                if (value === AgreementType.Default) {
                  handleUpdateAgreement({
                    customized: false,
                    partnershipTiers: defaultPartnershipTemplates.map((t) => ({
                      ...t,
                    })),
                  })
                } else if (value === AgreementType.Custom) {
                  handleUpdateAgreement({
                    customized: true,
                    partnershipTiers:
                      partnershipDetails.partnershipAgreement?.partnershipTiers,
                  })
                }
              }}
              hideOptionalText
            />
            {isDefaultAgreement ? (
              <div className="agx-dropdown-wrapper"></div>
            ) : (
              <AgxDropdown
                id="startingTier"
                options={PartnershipLevelOptions}
                selected={startingTierOption.value}
                disabled={isDefaultAgreement}
                label="Select starting tier"
                required
                onChange={(value) => {
                  const selectedLevel = value as PartnershipTierLevel
                  const existingTiers =
                    partnershipDetails.partnershipAgreement?.partnershipTiers ??
                    []
                  const mergedTiers = defaultPartnershipTemplates
                    .filter(
                      (t) =>
                        t.level !== undefined &&
                        selectedLevel !== undefined &&
                        tierOrder[t.level] >= tierOrder[selectedLevel]
                    )
                    .map(
                      (t) =>
                        existingTiers.find((et) => et.level === t.level) ?? {
                          ...t,
                        }
                    )
                  handleUpdateAgreement({ partnershipTiers: mergedTiers })
                }}
                hideOptionalText
              />
            )}
          </AgxRow>

          {/* Partnership Tiers Table */}
          <AgxColumn fill>
            <table
              className="partnershipTable"
              width="100%"
              style={{ paddingBottom: 30 }}
            >
              <thead>
                <tr>
                  <th align="left">
                    <AgxTypography id="tierHeader" type="title" size="small">
                      Partnership Tier
                    </AgxTypography>
                  </th>
                  <th align="left">
                    <AgxTypography
                      id="gciRangeHeader"
                      type="title"
                      size="small"
                    >
                      GCI range
                    </AgxTypography>
                  </th>
                  <th>&nbsp;</th>
                  <th align="left">
                    <AgxTypography
                      id="serviceFeeHeader"
                      type="title"
                      size="small"
                    >
                      UX Service Fee
                    </AgxTypography>
                  </th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {partnershipDetails.partnershipAgreement?.partnershipTiers?.map(
                  (
                    tier: PartnershipTier,
                    index: number,
                    arr: PartnershipTier[]
                  ) => {
                    const isFirst = index === 0
                    const isLast = index === arr.length - 1
                    let gciLabel = ''
                    if (isFirst && tier.maximumGci !== undefined)
                      gciLabel = 'Up to and including'
                    else if (isLast && tier.minimumGci !== undefined)
                      gciLabel = `From $${tier.minimumGci.toLocaleString()}`
                    else if (
                      tier.minimumGci !== undefined &&
                      tier.maximumGci !== undefined
                    )
                      gciLabel = `$${tier.minimumGci.toLocaleString()} to `

                    const updateTier = (updatedTier: Partial<typeof tier>) => {
                      const updatedTiers =
                        partnershipDetails.partnershipAgreement?.partnershipTiers?.map(
                          (t, i) => (i === index ? { ...t, ...updatedTier } : t)
                        )
                      handleUpdateAgreement({ partnershipTiers: updatedTiers })
                    }

                    return (
                      <tr key={index}>
                        <td>
                          <AgxRow
                            gap="spacing-sm"
                            padding={{
                              top: 'spacing-sm',
                              right: 'spacing-6xl',
                            }}
                            alignItems="alignCenter"
                          >
                            <Icon_PartnershipLevel
                              partnershipLevel={
                                tier.level as unknown as PartnershipDetailsPartnershipLevel
                              }
                              width={20}
                              color="var(--fg-tertiary)"
                            />
                            {
                              <AgxTypography
                                id="iconlabel"
                                type="body_ui"
                                size="small"
                              >
                                {
                                  PartnershipLevelLabels[
                                    tier.level as unknown as PartnershipDetailsPartnershipLevel
                                  ]
                                }
                              </AgxTypography>
                            }
                          </AgxRow>
                        </td>
                        <td>
                          <AgxRow
                            padding={{
                              top: 'spacing-sm',
                              right: 'spacing-6xl',
                            }}
                            alignItems="alignCenter"
                          >
                            <div>
                              <AgxTypography
                                id="gcilabel"
                                type="body_ui"
                                size="small"
                              >
                                {gciLabel}
                              </AgxTypography>
                            </div>
                          </AgxRow>
                        </td>
                        <td>
                          {!isLast && (
                            <AgxRow padding={{ right: 'spacing-6xl' }}>
                              <AgxTextInput
                                type="currency"
                                id={`${tier.level}MaxGci`}
                                defaultValue={tier.maximumGci?.toString() ?? ''}
                                onInputValueChange={({ value }) => {
                                  const numericValue = Number(
                                    value.replace(/[^0-9.-]+/g, '')
                                  )
                                  const newMax = isNaN(numericValue)
                                    ? undefined
                                    : numericValue
                                  const updatedTiers =
                                    partnershipDetails.partnershipAgreement?.partnershipTiers?.map(
                                      (t, i) => {
                                        if (i === index) {
                                          return { ...t, maximumGci: newMax }
                                        } else if (i === index + 1) {
                                          // update next tier’s min GCI to be max + 1
                                          return {
                                            ...t,
                                            minimumGci:
                                              newMax !== undefined
                                                ? newMax + 1
                                                : undefined,
                                          }
                                        }
                                        return t
                                      }
                                    )
                                  handleUpdateAgreement({
                                    partnershipTiers: updatedTiers,
                                  })
                                }}
                                noHeader
                                stretch
                                disabled={isDefaultAgreement}
                                required
                                errorText={
                                  !tier.maximumGci
                                    ? 'Enter GCI range'
                                    : undefined
                                }
                                noDecimalPoints
                              />
                            </AgxRow>
                          )}
                        </td>
                        <td>
                          <AgxRow padding={{ right: 'spacing-6xl' }}>
                            <AgxTextInput
                              type="percent"
                              id={`serviceFee${tier.level}`}
                              defaultValue={
                                tier.serviceFeePercentage?.toString() ?? ''
                              }
                              onInputValueChange={({ value }) => {
                                const numericValue = Number(
                                  value.replace(/[^0-9.-]+/g, '')
                                )
                                updateTier({
                                  serviceFeePercentage: isNaN(numericValue)
                                    ? undefined
                                    : numericValue,
                                })
                              }}
                              noHeader
                              errorText={
                                tier.serviceFeePercentage == null ||
                                tier.serviceFeePercentage < 0
                                  ? 'Enter service fee'
                                  : undefined
                              }
                              stretch
                              required
                              disabled={isDefaultAgreement}
                            />
                          </AgxRow>
                        </td>
                        <td>
                          {!isDefaultAgreement &&
                            (partnershipDetails.partnershipAgreement
                              ?.partnershipTiers?.length ?? 0) > 1 && (
                              <AgxButton
                                text=""
                                id={`btnCross${index}`}
                                size="small"
                                variant="image"
                                leftIcon={
                                  <Icon_CloseCircle color="var(--fg-tertiary)" />
                                }
                                onClick={() => onDeleteItemHandler(tier.level)}
                              />
                            )}
                        </td>
                      </tr>
                    )
                  }
                )}
              </tbody>
            </table>

            <AgxRow alignItems="flex-end" gap="spacing-2xl">
              {!isDefaultAgreement &&
                (partnershipDetails.partnershipAgreement?.partnershipTiers
                  ?.length ?? 0) < 5 && (
                  <AgxButton
                    id="add-tier"
                    text="Add Tier"
                    size="medium"
                    variant="primary"
                    onClick={onAddTierHandler}
                    rightIcon={
                      <Icon_PlusOutline
                        color="var(--fg-white)"
                        width={10}
                        height={10}
                      />
                    }
                  />
                )}
            </AgxRow>
          </AgxColumn>
        </AgxColumn>
      </Panel>
    </AgxColumn>
  )
}

export default PartnershipAgreement
